import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  Users, GraduationCap, Award, TrendingUp, Search, Download, ArrowRight, BookOpen, Mail, Gift, Ticket, KeyRound, Inbox, CalendarDays,
} from "lucide-react";

function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="card-tech p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-red-50 border border-red-100 grid place-items-center">
          <Icon className="w-4 h-4 text-red-600" strokeWidth={2.25} />
        </div>
        <div className="overline"><span className="red-bar" />{label}</div>
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight">{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1 font-mono">{hint}</div>}
    </div>
  );
}

export default function InstructorDashboard() {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | paid | unpaid | passed

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/admin/students"),
          api.get("/admin/stats"),
        ]);
        setStudents(s.data.students || []);
        setStats(st.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = students;
    if (filter === "paid") list = list.filter((s) => s.paid);
    else if (filter === "unpaid") list = list.filter((s) => !s.paid);
    else if (filter === "passed") list = list.filter((s) => s.final_exam_passed);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (s) => s.full_name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q),
      );
    }
    return list;
  }, [students, filter, query]);

  const exportCSV = () => {
    const headers = [
      "Full Name", "Email", "Paid", "Joined", "Hours Completed", "Total Hours",
      "Progress %", "Lessons Completed", "Quizzes Taken", "Final Exam %",
      "Final Passed", "Certificate ID",
    ];
    const rows = filtered.map((s) => [
      s.full_name,
      s.email,
      s.paid ? "Yes" : "No",
      new Date(s.joined).toISOString().slice(0, 10),
      s.hours_completed,
      s.total_hours,
      pct(s.hours_completed, s.total_hours),
      s.lessons_completed,
      s.quizzes_taken,
      s.final_exam_pct ?? "",
      s.final_exam_passed ? "Yes" : "No",
      s.certificate_id || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((cell) => {
            const v = String(cell ?? "");
            return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `apm-cohort-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Loading cohort…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-dashboard">
        <div className="overline mb-2"><span className="red-bar" />Instructor</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2" data-testid="instructor-title">
          Cohort progress
        </h1>
        <p className="text-zinc-600 mb-6 max-w-2xl">
          Track every enrolled student in real time — payments, lesson completion, quiz scores, and certificates issued.
        </p>

        {/* Instructor tools row */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="instructor-tools-row">
          <Link to="/instructor/comp-codes" className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 border-2 border-zinc-200 hover:border-red-600 hover:text-red-600 transition-colors inline-flex items-center gap-1.5">
            <Ticket className="w-3.5 h-3.5" /> Generate comp codes
          </Link>
          <Link to="/instructor/unlock" className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 border-2 border-zinc-200 hover:border-red-600 hover:text-red-600 transition-colors inline-flex items-center gap-1.5" data-testid="instructor-unlock-link">
            <KeyRound className="w-3.5 h-3.5" /> Manual unlock
          </Link>
          <Link to="/instructor/leads" className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 border-2 border-zinc-200 hover:border-red-600 hover:text-red-600 transition-colors inline-flex items-center gap-1.5" data-testid="instructor-leads-link">
            <Inbox className="w-3.5 h-3.5" /> Lead pipeline
          </Link>
          <Link to="/instructor/seminars" className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 border-2 border-zinc-200 hover:border-red-600 hover:text-red-600 transition-colors inline-flex items-center gap-1.5" data-testid="instructor-seminars-link">
            <CalendarDays className="w-3.5 h-3.5" /> Free Career Seminar
          </Link>
          <Link to="/instructor/email-logs" className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 border-2 border-zinc-200 hover:border-red-600 hover:text-red-600 transition-colors inline-flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Email logs
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Students" value={stats?.total_students ?? 0} hint="All registered accounts" />
          <StatCard icon={GraduationCap} label="Enrolled (paid)" value={stats?.paid_students ?? 0} hint={`${stats?.conversion_pct ?? 0}% conversion`} />
          <StatCard icon={Award} label="Certificates issued" value={stats?.certificates_issued ?? 0} hint="Final exam ≥ 70%" />
          <StatCard icon={TrendingUp} label="Cohort filtered" value={filtered.length} hint={`${filter} · ${query || "no search"}`} />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or email…"
              className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none pl-9 pr-3 py-2.5 text-sm font-mono bg-white"
              data-testid="instructor-search"
            />
          </div>
          <div className="flex gap-1 border-2 border-zinc-200 p-1 bg-white">
            {[
              { id: "all", label: "All" },
              { id: "paid", label: "Paid" },
              { id: "unpaid", label: "Unpaid" },
              { id: "passed", label: "Passed" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                  filter === f.id ? "bg-red-600 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`}
                data-testid={`instructor-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={exportCSV}
            disabled={!filtered.length}
            className="btn-outline disabled:opacity-50"
            data-testid="instructor-export-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <Link to="/instructor/email-logs" className="btn-outline" data-testid="instructor-email-logs-link">
            <Mail className="w-4 h-4" /> Email logs
          </Link>
          <Link to="/instructor/comp-codes" className="btn-outline" data-testid="instructor-comp-codes-link">
            <Gift className="w-4 h-4" /> Comp codes
          </Link>
        </div>

        {/* Table */}
        <div className="border-2 border-zinc-200 overflow-x-auto" data-testid="instructor-cohort-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-white text-left font-mono text-[11px] uppercase tracking-widest">
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Quizzes</th>
                <th className="px-4 py-3">Final Exam</th>
                <th className="px-4 py-3">Certificate</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-zinc-500" data-testid="instructor-empty">
                    No students match your filter.
                  </td>
                </tr>
              )}
              {filtered.map((s) => {
                const p = pct(s.hours_completed, s.total_hours);
                return (
                  <tr key={s.id} className="border-t border-zinc-100 hover:bg-red-50/40 transition-colors" data-testid={`instructor-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-950">{s.full_name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{s.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {s.paid ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider bg-green-100 text-green-800 px-2 py-1">
                          ENROLLED
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-1">
                          UNPAID
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-zinc-100">
                          <div className="h-full bg-red-600" style={{ width: `${p}%` }} />
                        </div>
                        <div className="font-mono text-xs text-zinc-700 w-10 text-right">{p}%</div>
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-1">
                        {s.hours_completed}h / {s.total_hours}h · {s.lessons_completed} lessons
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-700">{s.quizzes_taken}</td>
                    <td className="px-4 py-3">
                      {s.final_exam_pct !== null ? (
                        <span className={`font-mono font-bold ${s.final_exam_passed ? "text-green-700" : "text-red-600"}`}>
                          {s.final_exam_pct}%
                        </span>
                      ) : (
                        <span className="text-zinc-400 font-mono text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.certificate_id ? (
                        <span className="font-mono text-[11px] text-red-600">{s.certificate_id}</span>
                      ) : (
                        <span className="text-zinc-400 font-mono text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/instructor/student/${s.id}`}
                        className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-700 hover:text-red-600"
                        data-testid={`instructor-view-${s.id}`}
                      >
                        <BookOpen className="w-3 h-3" /> View <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
